import React, { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import * as Bixby from '$/core';
import * as Services from '$/services';
import { ReactComponent as PortalLinkIcon } from '$/assets/icons/PortalLinkIcon.svg';
import { DataSiteEmailTrackingData } from '$/core/models';
import {
  WorkflowManagementCommonState,
  DataSiteEmailTrackingGroupedRecordStatus,
  WorkflowManagementUploadToPortalState,
} from '$/core/enums';
import DocsCellRenderer from './cell-renderers/DocsCellRenderer';
import ReceivedClientsCellRenderer from './cell-renderers/ReceivedClientsCellRenderer';
import MissingClientsCellRenderer from './cell-renderers/MissingClientsCellRenderer';
import MarkClientsAsReceivedModal from './MarkClientsAsReceivedModal';

interface WorkflowGridProps {
  filteredTasks: DataSiteEmailTrackingData[];
  assigneeList?: Bixby.Models.IIdentifiable[];
  dirtyRows: Map<string, DataSiteEmailTrackingGroupedRecordStatus>;
  onStatusChange: (groupId: string, newStatus: DataSiteEmailTrackingGroupedRecordStatus) => void;
  onRefreshData: () => Promise<void>;
  dispatchToastError: (message: string) => void;
  dispatchToastSuccess: (message: string) => void;
  dispatchToastInfo: (message: string) => void;
}

const WorkflowGrid: React.FC<WorkflowGridProps> = ({
  filteredTasks,
  assigneeList,
  dirtyRows,
  onStatusChange,
  onRefreshData,
  dispatchToastError,
  dispatchToastSuccess,
  dispatchToastInfo,
}) => {
  const gridApiRef = useRef<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<DataSiteEmailTrackingData | null>(null);

  // Trigger row refresh when dirtyRows changes to update row classes
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.redrawRows();
    }
  }, [dirtyRows]);

  const handleOpenMarkAsReceivedModal = (taskData: DataSiteEmailTrackingData) => {
    setSelectedTask(taskData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTask(null);
  };

  const handleMarkClientsAsReceived = async (clientIds: string[], onStartFetching: () => void) => {
    if (!selectedTask) return;

    try {
      await Services.DataSiteService.markClientsAsReceived(selectedTask.groupId, clientIds);
      dispatchToastSuccess(`Successfully marked ${clientIds.length} client(s) as received`);
      dispatchToastInfo('Fetching updated rows...');
      onStartFetching();
      await onRefreshData();
    } catch (error) {
      dispatchToastError('Failed to mark clients as received. Please try again.');
      throw error;
    }
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'name') {
      return [];
    }
    return ['copy', 'copyWithHeaders', 'separator', 'export'];
  };

  const linkCellFormatter = (params: any) => {
    const url = `/admin/company/${params.data['companyId']}/document-hosting/upload/${params.data['emailId'] ?? ''}`;
    const hasMissingClients = params.data.missingClients && params.data.missingClients.length > 0;

    return (
      <div className="workflow-management-view-portal">
        <PortalLinkIcon />
        {hasMissingClients ? (
          <span style={{ cursor: 'not-allowed', opacity: 0.5 }}>Upload To Portal</span>
        ) : (
          <a href={url}>Upload To Portal</a>
        )}
      </div>
    );
  };

  const dateTimeCellRenderer = (params: any) => {
    const date = new Date(params.value);
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return (
      <div
        style={{
          lineHeight: '1.2',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
        }}>
        <div>{dateStr}</div>
        <div style={{ fontSize: '0.85em' }}>{timeStr}</div>
      </div>
    );
  };

  const portalLinkCellRenderer = (params: any) => {
    return (
      <div className="workflow-management-view-portal">
        <PortalLinkIcon />
        <a href={params.value}>View Portal</a>
      </div>
    );
  };

  const handleCellValueChanged = (params: any) => {
    if (params.colDef.field === 'status') {
      const groupId = params.data.groupId;
      const newStatus = params.newValue as DataSiteEmailTrackingGroupedRecordStatus;
      onStatusChange(groupId, newStatus);
    }
  };

  const onGridReady = (params: any) => {
    gridApiRef.current = params.api;
  };

  return (
    <div className="workflow_management_data_grid">
      <div className="grid_container">
        <div className="grid ag-theme-alpine">
          <AgGridReact
            enableRangeSelection={true}
            getContextMenuItems={getContextMenuItems}
            onCellValueChanged={handleCellValueChanged}
            onGridReady={onGridReady}
            rowClassRules={{
              'workflow-row-dirty': (params: any) => {
                return params.data && dirtyRows.has(params.data.groupId);
              },
            }}
            defaultColDef={{
              flex: 1,
              minWidth: 200,
              sortable: true,
              filter: 'agTextColumnFilter',
              cellClass: 'align-left',
            }}
            rowData={filteredTasks}
            suppressDragLeaveHidesColumns={true}
            suppressMovableColumns={true}
            animateRows={true}
            rowHeight={35}
            stopEditingWhenCellsLoseFocus={true}
            headerHeight={35}
            columnDefs={[
              {
                field: 'clientName',
                headerName: 'Client Name',
              },
              {
                field: 'dataSiteName',
                headerName: 'Data Site Name',
                cellClass: 'align-left',
              },
              { field: 'bixbyId', headerName: 'Bixby Id', sort: 'asc' },
              {
                field: 'provider',
                headerName: 'Provider',
                minWidth: 110,
              },
              {
                field: 'docCount',
                headerName: 'Doc Count',
                minWidth: 150,
                cellDataType: 'number',
                cellClass: 'align-center',
                hide: true,
              },
              {
                field: 'docNames',
                headerName: 'Documents',
                autoHeight: true,
                cellRenderer: DocsCellRenderer,
              },
              {
                field: 'newsType',
                headerName: 'News Type',
                minWidth: 120,
              },
              {
                field: 'receivedClients',
                headerName: 'Received Clients',
                width: 100,
                autoHeight: true,
                cellRenderer: ReceivedClientsCellRenderer,
              },
              {
                field: 'missingClients',
                headerName: 'Missing Clients',
                width: 100,
                autoHeight: true,
                cellRenderer: MissingClientsCellRenderer,
                cellRendererParams: {
                  onOpenMarkAsReceivedModal: handleOpenMarkAsReceivedModal,
                },
              },
              {
                field: 'emailReceivedDate',
                headerName: 'Email Received',
                minWidth: 120,
                autoHeight: true,
                cellDataType: 'dateTime',
                cellRenderer: dateTimeCellRenderer,
              },
              {
                field: 'portalLink',
                headerName: 'Upload To Portal',
                width: 100,
                cellRenderer: linkCellFormatter,
              },
              {
                field: 'assignee',
                headerName: 'Assignee',
                width: 100,
                editable: true,
                floatingFilter: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: assigneeList?.map(_ => _.id) || [],
                },
                hide: true,
              },
              {
                field: 'assignTask',
                headerName: 'Assign Task',
                width: 100,
                hide: true,
              },
              {
                field: 'uploadToPortal',
                headerName: 'Upload To Portal',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: Object.values(WorkflowManagementUploadToPortalState),
                },
                hide: true,
              },
              {
                field: 'transcription',
                headerName: 'Transcription',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: Object.values(WorkflowManagementCommonState),
                },
                hide: true,
              },
              {
                field: 'modelUpdate',
                headerName: 'Model Update',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: Object.values(WorkflowManagementCommonState),
                },
                hide: true,
              },
              {
                field: 'portalLink',
                headerName: 'Portal Link',
                width: 100,
                cellRenderer: portalLinkCellRenderer,
                hide: true,
              },
              {
                field: 'status',
                headerName: 'Status',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                floatingFilter: true,
                cellEditorParams: {
                  values: Object.values(DataSiteEmailTrackingGroupedRecordStatus),
                },
              },
              {
                field: 'completedAt',
                headerName: 'Completed At',
                width: 100,
                hide: true,
              },
            ]}
          />
        </div>
      </div>
      {selectedTask && (
        <MarkClientsAsReceivedModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onMarkAsReceived={handleMarkClientsAsReceived}
          bixbyId={selectedTask.bixbyId}
          dataSiteName={selectedTask.dataSiteName}
          provider={selectedTask.provider}
          docNames={selectedTask.docNames}
          missingClients={selectedTask.missingClients}
        />
      )}
    </div>
  );
};

export default WorkflowGrid;
